import {Component, Input, OnInit, Optional} from '@angular/core';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent} from "@components";
import {
  CompetitorResults,
  CompetitorStageDetail,
  CompetitorStatus, EventItem,
  FINISH_CONTROL_POINT_TYPE, Lookups, MeasurementTypes,
  Race, sportsTypeVisibilityMap, SportType, SportTypeFieldsVisibility, VisibilityTypes
} from "@model";
import {parseDurationToMs} from "@utility";

/** One row in a checkpoint/finish's per-team-member breakdown table. */
export interface TeamMemberPointRow {
  member: CompetitorResults;
  detail?: CompetitorStageDetail;
  /** True for the (slowest-arriving) member whose time the team's own row is based on. */
  determines: boolean;
}

@Component({
  selector: 'app-results-details-modal',
  templateUrl: './results-details-modal.component.html',
  styleUrl: './results-details-modal.component.scss',
  standalone: false
})
export class ResultsDetailsModalComponent extends AbstractComponent implements OnInit {
  @Input() competitorResults!: CompetitorResults;
  @Input() measurementType: number | undefined = undefined;
  @Input() category: number | undefined = undefined;
  @Input() gender: number | undefined = undefined;
  @Input() isRoundRace:boolean = false;
  @Input() showDistance:boolean = false;
  @Input() race?:Race;
  @Input() event?:EventItem;
  @Input({ required: true }) languages: Lookups[] = [];
  @Input() isAdmin: boolean = false;

  isTeam = false;

  list: CompetitorStageDetail[] = [];
  checkpointsIds: string[] = [];
  sgsFinishItem: CompetitorStageDetail | undefined;
  SGS?: { [key: string]: CompetitorStageDetail };
  sportTypeFieldsVisibility: SportTypeFieldsVisibility | undefined;

  constructor(@Optional() public activeModal: NgbActiveModal) {
    super();
  }


  showTeamMembers = false;
  finishSgsKey?: string;
  teamMemberRowsByPoint: Record<string, TeamMemberPointRow[]> = {};
  expanded: Record<string, boolean> = {};

  ngOnInit() {
    if (this.race?.sport) {
      this.sportTypeFieldsVisibility = sportsTypeVisibilityMap[this.race.sport as SportType];
    }
    this.isTeam = !!(this.competitorResults?.competitorResultsDetail?.team && this.competitorResults?.competitorResultsDetail?.team.length);
    this.showTeamMembers = !!(this.competitorResults?.teamMembersResult && this.competitorResults.teamMembersResult.length);
    if (this.competitorResults.competitorResultsDetail?.SGS && !this.isRoundRace) {
      this.SGS = this.competitorResults.competitorResultsDetail.SGS;
      this.checkpointsIds = Object.entries(this.SGS).map(([key, value]) => ({ ...value, cp_id: key }))
          .filter((item) => item.tp !== FINISH_CONTROL_POINT_TYPE)
          .sort((a, b) => {
            const sortA = a.ordinalNumber ?? 0;
            const sortB = b.ordinalNumber ?? 0;
            return sortA - sortB;
          }).map((item) => item.cp_id!);
      this.sgsFinishItem = Object.values(this.SGS).find((item) => item.tp === FINISH_CONTROL_POINT_TYPE);
      this.checkpointsIds.forEach(id => {
        this.expanded[id] = false;
      });
      if (this.sgsFinishItem) {
        this.expanded['finish'] = false
      }
    } else if (this.competitorResults.competitorResultsDetail?.laps && this.isRoundRace) {
      const items: CompetitorStageDetail[] = Object.entries(this.competitorResults.competitorResultsDetail.laps).map(([key, value]) => ({ ...value, lp:Number(key) }));
      this.list = items
          .sort((a, b) => a.lp! - b.lp!);
      this.list.forEach(l => {
        this.expanded[l.lp!] = false;
      });
    }
    if (this.showTeamMembers) {
      this.buildTeamMemberRowsByPoint();
    }
  }


  private buildTeamMemberRowsByPoint(): void {
    const members = this.competitorResults?.teamMembersResult ?? [];
    const keys = [...this.checkpointsIds];
    if (this.finishSgsKey) {
      keys.push(this.finishSgsKey);
    }

    keys.forEach(key => {
      const rows: TeamMemberPointRow[] = members.map(member => ({
        member,
        detail: member.competitorResultsDetail?.SGS?.[key],
        determines: false
      }));

      let determiningRow: TeamMemberPointRow | undefined;
      let determiningMs = NaN;

      for (const row of rows) {
        const ms = parseDurationToMs(row.detail?.nt_tm);
        if (!isNaN(ms) && (isNaN(determiningMs) || ms > determiningMs)) {
          determiningRow = row;
          determiningMs = ms;
        }
      }
      if (determiningRow) {
        determiningRow.determines = true;
      }

      rows.sort((a, b) => {
            return parseDurationToMs(b.detail?.nt_tm) - parseDurationToMs(a.detail?.nt_tm)
          }
      );

      this.teamMemberRowsByPoint[key] = rows;
    });
  }

  onClose() {
    this.activeModal.close();
  }

  protected readonly CompetitorStatus = CompetitorStatus;
  protected readonly VisibilityTypes = VisibilityTypes;
  protected readonly MeasurementTypes = MeasurementTypes;
}
